'use client';

import { useRef, useState } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { useSubmit } from '@formspree/react';
import { LibraryIcon } from '@repo/icons/library-icon';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import satori from 'satori';

import { Link } from '~/components/common/link';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { FORMSPREE_HAPPY_GRAM } from '~/lib/constants';
import { cn } from '~/lib/utils';
import { getIconCode, loadEmoji } from '~/utils/twemoji';

import { submitEmailCollect } from './actions';
import { Card } from './card';
import { CARDS } from './cards';

const MESSAGE_CHAR_LIMIT = 200;

export type FormValues = {
  first_name: string;
  last_name: string;
  email: string;
  card: string;
  file: File | FileList;
  message: string;
  updates: string;
  form_id: string;
  slug: string;
  g0tcha: string;
};

export function HappyGramForm({ buttonText = 'Create Happy Gram' }: { buttonText?: string | null }) {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState(0);

  const pathname = usePathname();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setError,
    setValue,
    getValues,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      message: '',
      updates: '',
      card: '',
      form_id: 'email-collection-general',
      slug: pathname,
      g0tcha: '',
    },
  });

  const messageCharCount = watch('message').length;

  // Formspree submit
  const submit = useSubmit<FieldValues & FormValues>(FORMSPREE_HAPPY_GRAM, {
    extraData: {
      date_submitted: new Date().toISOString(),
    },
  });

  const handleImageAttachment = async () => {
    try {
      // Convert JSX to SVG
      const svg = await satori(<Card message={getValues('message')} card={getValues('card')} size='lg' />, {
        width: 800,
        height: 560,
        fonts: [
          {
            name: 'RobotoMedium',
            data: await fetch('/Roboto-Medium.ttf').then((res) => res.arrayBuffer()),
            weight: 700,
            style: 'normal',
          },
        ],
        loadAdditionalAsset: async (code: string, segment: string) => {
          if (code === 'emoji') {
            return `data:image/svg+xml;base64,${btoa(await loadEmoji('twemoji', getIconCode(segment)))}`;
          }

          return code;
        },
      });

      // Send SVG to API route to convert to PNG
      const response = await fetch('/api/satori', {
        method: 'POST',
        body: JSON.stringify({ svg }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const blob = await response.blob();
        const file = new File([blob], 'generated-card-image.png', { type: 'image/png' });

        console.log('File created:', file);

        // Update form value directly without using fileInputRef
        setValue('file', file);
        console.log('Form value updated with file:', file);

        // Optionally, if you still want to update the file input for consistency:
        if (fileInputRef.current) {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          fileInputRef.current.files = dataTransfer.files;
          console.log('File input updated:', fileInputRef.current.files);
        } else {
          console.log('fileInputRef is null, but form value was still updated');
        }
      } else {
        console.error('Failed to convert SVG to PNG');
      }
    } catch (error) {
      console.error('Error in handleImageAttachment:', error);
    }
  };

  // Submit to email collection endpoint if user opted in
  const submitEmailCollection = async (formData: FormValues) => {
    if (!formData.updates) return;

    const result = await submitEmailCollect(formData);

    if (result.success) {
      console.log(result.message);
    } else {
      console.error(result.message);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    console.log('Starting form submission', data);
    try {
      // Ensure the file is attached
      await handleImageAttachment();

      // Get the updated form data after handleImageAttachment
      const updatedData = getValues();
      console.log('Updated form data after handleImageAttachment:', updatedData);

      const formData = new FormData();

      // Append form data including file
      for (const [key, value] of Object.entries(updatedData)) {
        if (key === 'file') {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (value instanceof FileList && value.length > 0) {
            formData.append(key, value[0]);
          }
        } else {
          formData.append(key, value as string);
        }
      }

      // Check if file is present
      const fileInFormData = formData.get('file');

      if (!fileInFormData) {
        console.error('File is missing from FormData');
        throw new Error('File is missing');
      } else {
        console.log('File found in FormData:', fileInFormData);
      }

      console.log('Submitting form data');
      // Submit to email collection if opted in
      await submitEmailCollection(data);

      // Submit to Formspree
      const result = await submit(formData);
      if (result.kind === 'success') {
        setStep(2);
        return;
      }

      for (const { code, message } of result.getFormErrors()) {
        setError(`root.${code}`, {
          type: code,
          message,
        });
      }

      for (const [field, fieldErrors] of result.getAllFieldErrors()) {
        setError(field as keyof FormValues, {
          message: fieldErrors.map((error) => error.message).join(', '),
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Handle error (e.g., show error message to user)
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className='border-magenta font-greycliff-extrabold text-primary disabled:border-asphalt-25 disabled:bg-asphalt-25 disabled:text-asphalt-50 mx-auto rounded-full border-2 bg-transparent px-6 py-4 uppercase disabled:cursor-not-allowed'>
        {buttonText}
      </DialogTrigger>
      <DialogContent className='p-0! flex h-[96svh] max-h-[644px] max-w-[640px] flex-col overflow-hidden'>
        <form onSubmit={handleSubmit(onSubmit)} className='flex h-full flex-col'>
          {/* Honeypot */}
          <div hidden>
            <label htmlFor='g0tcha'>g0tcha</label>
            <input aria-label='g0tcha' type='text' {...register('g0tcha')} />
          </div>
          {/* Pardot hidden form fields START */}
          <div hidden>
            <label htmlFor='form_id'>Form ID</label>
            <Input readOnly type='text' {...register('form_id')} />
          </div>
          <div hidden>
            <label htmlFor='slug'>Slug</label>
            <Input readOnly type='text' {...register('slug')} />
          </div>
          {/* Pardot hidden form fields END */}
          {step !== 2 && (
            <DialogHeader>
              <DialogTitle className='bg-blue font-greycliff-bold px-10 py-6 text-3xl text-white'>
                {step === 0 ? 'Send a card to a Starlight family' : 'Review your card'}
              </DialogTitle>
            </DialogHeader>
          )}

          <div className={cn('flex-1 overflow-y-auto ', step === 2 ? '' : 'px-10 py-6')}>
            {step === 0 && (
              <>
                <Textarea
                  type='text'
                  placeholder='Write your message (200 character max)'
                  required
                  {...register('message', { maxLength: MESSAGE_CHAR_LIMIT })}
                />
                <div className='body-sm mb-4 mt-1 flex justify-between'>
                  <div className={messageCharCount > MESSAGE_CHAR_LIMIT ? 'text-red' : ''}>
                    {messageCharCount}/{MESSAGE_CHAR_LIMIT}
                  </div>
                  <div>{MESSAGE_CHAR_LIMIT} Character Limit</div>
                </div>

                <Label className='text-asphalt-50 mb-2 block' htmlFor='file'>
                  Choose your card:
                </Label>

                <input type='file' {...register('file')} className='hidden' ref={fileInputRef} />

                <div className='-mt-2 grid grid-cols-3 gap-6'>
                  {CARDS.map((layout) => {
                    return (
                      <div key={layout.title} className='relative'>
                        <input
                          type='radio'
                          id={layout.title}
                          value={layout.title}
                          checked={watch('card') === layout.title}
                          {...register('card', { required: 'Card variant is required' })}
                          onClick={() => {
                            setValue('card', layout.title);
                          }}
                          className='peer sr-only'
                        />
                        <label
                          htmlFor={layout.title}
                          className='peer-checked:border-blue flex cursor-pointer flex-col items-center justify-center border-2 border-transparent transition-colors'
                        >
                          <Image src={layout.image} width={328} height={458} alt={layout.title} />
                        </label>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className='mx-auto max-h-[280px] max-w-[400px] shadow-lg'>
                  <Card message={getValues('message')} card={getValues('card')} size='sm' />
                </div>

                <div className='mt-8 grid grid-cols-2 gap-x-4'>
                  <div>
                    <Input
                      type='text'
                      placeholder='First Name'
                      {...register('first_name', { required: 'First Name is required' })}
                      className='mb-2 '
                    />
                    {errors.first_name && <p className='text-red mb-4 mt-2'>{errors.first_name.message}</p>}
                  </div>
                  <div>
                    <Input
                      type='text'
                      placeholder='Last Name'
                      {...register('last_name', { required: 'Last Name is required' })}
                      className='mb-2'
                    />
                    {errors.last_name && <p className='text-red mb-4 mt-2'>{errors.last_name.message}</p>}
                  </div>
                </div>
                <Input
                  type='email'
                  placeholder='Email'
                  {...register('email', { required: 'Email is required' })}
                  className='mt-4'
                />
                {errors.email && <p className='text-red mb-4 mt-2'>{errors.email.message}</p>}
                {/* TODO: This currently does nothing, add to salesforce endpoint */}
                <Checkbox
                  label='I would like to receive updates about my impact.'
                  {...register('updates')}
                  className='mt-4'
                />
                {errors.updates && <p className='text-red mb-4 mt-2'>{errors.updates.message}</p>}
              </>
            )}

            {step === 2 && (
              <div className='relative text-center'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='640'
                  height='253'
                  viewBox='0 0 640 253'
                  fill='none'
                  className='-z-10'
                >
                  <path
                    d='M735 91.5C735 180.694 549.198 253 320 253C90.8018 253 -95 180.694 -95 91.5C-95 -225 -95 -225 320 -225C735 -225 735 -225 735 91.5Z'
                    fill='#0071CE'
                  />
                </svg>
                <DialogHeader className='absolute inset-0 mt-12'>
                  <DialogTitle className='font-greycliff-bold text-yellow text-center text-6xl'>Thank you!</DialogTitle>
                  <DialogDescription className='text-center text-lg text-white'>
                    We’ll send your card to a hospitalized kid and their family.
                  </DialogDescription>
                </DialogHeader>
                <div className='isolate mx-auto mb-8'>
                  <Image
                    src='/img/happy-grams/thank-you.png'
                    width={144}
                    height={144}
                    alt='Starlight Children'
                    className='border-yellow mx-auto -mt-16 rounded-full border-8 border-solid object-cover text-center'
                  />
                </div>
                <LibraryIcon use='Rays' className='text-yellow mx-auto' width={48} height={24} />
                <h4 className='font-greycliff-bold text-foreground mt-2 text-3xl'>Include a toy with your card?</h4>
                <p className='font-greycliff-medium text-asphalt-50 mt-4 px-10'>
                  For an even bigger impact, you can send a toy along with your Happy Gram. For just $25, you can
                  support the delivery of a toy to a child in the hospital. Together, we can ensure no child feels
                  forgotten this holiday season.
                </p>
                <Button className='font-greycliff-extrabold isolate mt-8' asChild>
                  <Link href='/give-happiness'>Give $25 Today</Link>
                </Button>
              </div>
            )}
          </div>

          {step !== 2 && (
            <DialogFooter className='bg-linear-to-t mt-auto flex justify-between border-t border-t-[#EBEBEB] from-white px-5 py-3 sm:px-10 sm:py-5'>
              {step === 0 && (
                <Button
                  onClick={() => setStep(step + 1)}
                  type='button'
                  disabled={!watch('card') || !watch('message') || messageCharCount > MESSAGE_CHAR_LIMIT}
                >
                  Next
                </Button>
              )}

              {step === 1 && (
                <>
                  <Button type='submit' disabled={isSubmitting || isSubmitSuccessful}>
                    Submit
                  </Button>

                  <Button
                    variant='ghost'
                    onClick={() => {
                      setStep(Math.max(0, step - 1));
                    }}
                    type='button'
                  >
                    Back
                  </Button>
                </>
              )}
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
